import { baseApi } from '../api/baseApi';
import type {
    DomainVerificationInitiateRequest,
    DomainVerificationInitiateResponse,
    DomainVerificationCheckRequest,
    DomainVerificationCheckResponse,
    DomainVerificationListResponse,
    DomainVerificationInstructionsResponse,
    DomainVerificationDeleteResponse,
} from '../api/types';

// Extend the base API with domain verification endpoints
export const domainVerificationApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        // POST /domains/verify/initiate/ - Initiate domain verification process
        initiateDomainVerification: builder.mutation<DomainVerificationInitiateResponse, DomainVerificationInitiateRequest>({
            query: (data) => ({
                url: '/domains/verify/initiate/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [{ type: 'DomainVerification', id: 'LIST' }],
        }),

        // POST /domains/verify/check/ - Check domain verification status and attempt verification
        checkDomainVerification: builder.mutation<DomainVerificationCheckResponse, DomainVerificationCheckRequest>({
            query: (data) => ({
                url: '/domains/verify/check/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [{ type: 'DomainVerification', id: 'LIST' }],
        }),

        // GET /domains/verify/list/ - List organization's domain verifications
        getDomainVerifications: builder.query<DomainVerificationListResponse, void>({
            query: () => ({
                url: '/domains/verify/list/',
                method: 'GET',
            }),
            providesTags: (result) =>
                result
                    ? [
                        ...result.verifications.map(({ id }) => ({ type: 'DomainVerification' as const, id })),
                        { type: 'DomainVerification', id: 'LIST' },
                    ]
                    : [{ type: 'DomainVerification', id: 'LIST' }],
        }),

        // GET /domains/verify/instructions/{verification_id}/ - Get verification instructions
        getDomainVerificationInstructions: builder.query<DomainVerificationInstructionsResponse, number>({
            query: (verificationId) => ({
                url: `/domains/verify/instructions/${verificationId}/`,
                method: 'GET',
            }),
            providesTags: (_result, _error, verificationId) => [
                { type: 'DomainVerification', id: verificationId },
            ],
        }),

        // DELETE /domains/delete/{verification_id}/ - Delete domain verification
        deleteDomainVerification: builder.mutation<DomainVerificationDeleteResponse, number>({
            query: (verificationId) => ({
                url: `/domains/delete/${verificationId}/`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'DomainVerification', id: 'LIST' }],
        }),
    }),
    overrideExisting: false,
});

// Export hooks for usage in components
export const {
    useInitiateDomainVerificationMutation,
    useCheckDomainVerificationMutation,
    useGetDomainVerificationsQuery,
    useLazyGetDomainVerificationInstructionsQuery,
    useDeleteDomainVerificationMutation,
} = domainVerificationApi;